import { log, error } from '@/services/Log'
import {
  object,
  CheckerReturnType,
  number,
  string,
  array,
  bool,
  nullable,
  assertion,
} from '@recoiljs/refine'
import {
  COGNITO_REGION,
  COGNITO_IDENTITY_POOL_ID,
  FIREHOSE_REGION,
  FIREHOSE_DELIVERY_STREAM_NAME,
} from '@/services/Configuration'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { thenCaught } from '@/helpers/FP'

const Event = object({
  uuid: string(),
  userUUID: string(),
  date: string(),
  eventType: string(),
  url: string(),
  items: array(object({
    sku: string(),
    salePrice: number(),
    price: number(),
    quantity: number(),
  })),
  context: array(
    object({
      key: string(),
      value: object({
        stringValue: nullable(string()),
        numberValue: nullable(number()),
        booleanValue: nullable(bool()),
      }),
    }),
  ),
})

const assertEvent = assertion(Event, 'Invalid DMP Analytics Event')

export type DMPAnalyticsEvent = CheckerReturnType<typeof Event>
export type DMPAnalyticsEventContext = DMPAnalyticsEvent['context']
export type DMPAnalyticsEventItem = DMPAnalyticsEvent['items'][number]
type FirehoseClient = InstanceType<Awaited<typeof import('@aws-sdk/client-firehose')>['FirehoseClient']>

let client: null | FirehoseClient = null

type MinEventData = Omit<DMPAnalyticsEvent, 'uuid' | 'userUUID' | 'date' | 'url'> & {
  uuid?: DMPAnalyticsEvent['uuid']
  url?: DMPAnalyticsEvent['url']
}
const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/
const localStorageDMPUserUUIDKey = 'dmpUserUUID'
const getLocalStorageUserUUID = () => {
  try {
    const value = localStorage.getItem(localStorageDMPUserUUIDKey)
    if (value && uuidRegex.test(value)) {
      return value
    }
  } catch {
    return null
  }
  return null
}
// cached userUUID to avoid calling localStorage which may throw an error
let cachedUserUUID: string | null = null
const loadUserUUID = (uuidGenerator: () => string) => {
  if (cachedUserUUID) {
    return cachedUserUUID
  }
  const fromLocalStorage = getLocalStorageUserUUID()
  if (fromLocalStorage) {
    cachedUserUUID = fromLocalStorage
    return fromLocalStorage
  }
  const generated = uuidGenerator()
  localStorage.setItem(localStorageDMPUserUUIDKey, generated)
  cachedUserUUID = generated
  return generated
}

const sendEventHappyPath = async (event: MinEventData) => {
  const [
    { FirehoseClient, PutRecordBatchCommand },
    { fromCognitoIdentityPool },
    { v4: uuidv4 },
    { getComposeAssignmentMap },
    { trackAmplitudeFromDMPEvent },
  ] = await allPromisesWithRetries(() => [
    import('@aws-sdk/client-firehose'),
    import('@aws-sdk/credential-providers'),
    import('uuid'),
    import('@/helpers/getABExperiment'),
    import('@/hooks/DmpAnalytics/trackAmplitudeFromDMPEvent'),
  ])
  const eventData = assertEvent({
    url: window.location.href,
    uuid: String(event.uuid || uuidv4?.()),
    userUUID: loadUserUUID(uuidv4),
    date: new Date().toISOString(),
    ...event,
    context: [
      ...event.context,
      {
        key: 'A/B tests',
        value: { stringValue: JSON.stringify(getComposeAssignmentMap()) },
      },
    ],
  })
  client = client || new FirehoseClient({
    region: FIREHOSE_REGION,
    credentials: fromCognitoIdentityPool({
      clientConfig: { region: COGNITO_REGION },
      identityPoolId: COGNITO_IDENTITY_POOL_ID,
    }),
  })
  log('sendDMPAnalyticsEvent: Firehose client created')
  if (client) {
    log('Sending page view')
    const sendFirehosePromise = client.send(new PutRecordBatchCommand({
      DeliveryStreamName: FIREHOSE_DELIVERY_STREAM_NAME,
      Records: [
        {
          Data: Buffer.from(JSON.stringify(eventData)),
        },
      ],
    }))
    const amplitudePromise = trackAmplitudeFromDMPEvent(eventData)
    const results = await Promise.allSettled([
      sendFirehosePromise,
      amplitudePromise,
    ])
    results.forEach((result) => {
      if (result.status === 'rejected') {
        error('result', result)
      }
    })
    log('sendDMPAnalyticsEvent: Page view sent')
  }
}

const sendEventSadPath = (err: unknown) => {
  error(`sendDMPAnalyticsEvent: Error sending event: ${String(err)}`)
}

export const sendDMPAnalyticsEvent = thenCaught(sendEventHappyPath)(sendEventSadPath)
